import { Button, Section } from 'react-scale/components';
import { gsap } from 'react-scale/lib/gsap';
import * as React from 'react';
import clsx from 'clsx';
import { Drawer } from '../drawer';
import useMedia from 'react-use/lib/useMedia';

const lightBg = 'linear-gradient(180deg, #E5E2D7 3.49%, #FFFFFF 62.53%)';
const darkBg = '#171717';

export const Schedule = ({ preliminarySchedule = [] }) => {
  const [currentSlide, setCurrentSlide] = React.useState(0);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const isDesktop = useMedia('(min-width: 1024px)');
  const mainAxis: [string, string] = React.useMemo(
    () => (isDesktop ? ['width', 'minWidth'] : ['height', 'minHeight']),
    [isDesktop]
  );

  const togglePointerEvents = React.useCallback((pointerEvents = 'all') => {
    if (containerRef.current) {
      containerRef.current.style.pointerEvents = pointerEvents;
    }
  }, []);

  const handleSlideChange = (slide: number, duration = 0.3) => {
    if (currentSlide === slide) return;
    if (slide >= preliminarySchedule.length) {
      slide = 0;
    } else if (slide < 0) {
      slide = preliminarySchedule.length - 1;
    }
    const nextSlide = document.getElementById(`card-schedule-${slide}`);
    const prevSlide = document.getElementById(`card-schedule-${currentSlide}`);

    togglePointerEvents('none');
    const tl = gsap.timeline({
      onComplete: togglePointerEvents,
      defaults: { duration }
    });

    prevSlide
      .querySelector('.text-gradient-black')
      .classList.replace('text-gradient-black', 'text-gradient-white');
    nextSlide
      .querySelector('.text-gradient-white')
      .classList.replace('text-gradient-white', 'text-gradient-black');

    tl.to(prevSlide.querySelector('.schedule-list'), {
      autoAlpha: 0,
      duration: 0.2
    })
      .to(nextSlide, {
        [mainAxis[1]]: '50%',
        background: lightBg,
        color: 'black',
        ease: 'power4.out',
        duration: duration + 0.1
        // boxShadow: initialBoxShadow
      })
      .to(
        prevSlide,
        {
          [mainAxis[1]]: 0,
          [mainAxis[0]]: '33%',
          background: darkBg,
          color: 'white',
          ease: 'power4.out',
          duration: duration + 0.1
        },
        '<'
      );

    tl.fromTo(
      nextSlide.querySelector('.schedule-list'),
      { autoAlpha: 0, y: 20 },
      {
        autoAlpha: 1,
        y: 0,
        ease: 'power4.inOut'
      },
      mainAxis[0] === 'width' ? '<' : '=0.1'
    );

    setCurrentSlide(slide);
  };

  React.useEffect(() => {
    const tl = gsap.timeline();
    const rest = Array.from(containerRef.current.querySelectorAll('[id^=card-schedule-]'));
    const currentSlideRef = document.getElementById(`card-schedule-${currentSlide}`);
    tl.set(currentSlideRef, {
      [mainAxis[1]]: '50%',
      background: lightBg,
      color: 'black'
    })
      .set(currentSlideRef.querySelector('.schedule-list'), { autoAlpha: 1 })
      .set(rest, {
        [mainAxis[0] === 'width' ? 'height' : 'width']: '100%'
      })
      .set(
        rest.filter(item => item.id !== `card-schedule-${currentSlide}`),
        {
          [mainAxis[0]]: '33%',
          [mainAxis[1]]: 0
        }
      );

    currentSlideRef
      .querySelector('.text-gradient-white')
      ?.classList.replace('text-gradient-white', 'text-gradient-black');

    return () => {
      tl.kill();
    };
  }, [isDesktop]);

  React.useEffect(() => {
    if (!isDesktop) return;

    let interval: NodeJS.Timer = setInterval(
      () =>
        handleSlideChange(
          currentSlide + 1 >= preliminarySchedule.length ? 0 : currentSlide + 1,
          0.6
        ),
      5000
    );
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        clearInterval(interval);
        return;
      }
      interval = setInterval(
        () =>
          handleSlideChange(
            currentSlide + 1 >= preliminarySchedule.length ? 0 : currentSlide + 1,
            0.6
          ),
        5000
      );
    };

    window.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      clearInterval(interval);
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentSlide, isDesktop]);

  return (
    <Section className="flex flex-col items-center gap-y-12 lg:gap-y-[90px] z-10 relative">
      <h3 className="font-forest text-[45px] lg:text-[64px] text-center">
        <span className="hidden lg:inline">Preliminary</span> Schedule
      </h3>

      <div
        ref={containerRef}
        className={`group relative h-[1000px] lg:h-[600px] bg-[${darkBg}] lg:bg-transparent flex-col lg:flex-row flex gap-x-3 lg:mb-[25px] w-full rounded-3xl`}
      >
        {preliminarySchedule.map((dayCard, idx) => (
          <article
            id={`card-schedule-${idx}`}
            onClick={() => handleSlideChange(idx)}
            key={dayCard.date}
            style={{ WebkitTapHighlightColor: 'transparent' }}
            className={clsx(
              currentSlide !== idx && 'cursor-pointer',
              `relative lg:border lg:border-[#B7B7B7] transition-[transform] bg-[${darkBg}] rounded-3xl flex flex-col px-8 py-[30px] gap-y-2`,
              'first:shadow-[inset_0_4px_0_rgb(0_0_0_/_0.2)] lg:shadow-[inset_0_4px_0_rgb(0_0_0_/_0.2)]'
            )}
          >
            <h4 className="leading-[1.1] text-[32px]">{dayCard.date}</h4>
            <span className="text-gradient-white uppercase w-fit text-sm">{dayCard.day}</span>
            <ul className="schedule-list flex gap-y-6 flex-col mt-auto opacity-0">
              {dayCard.items.map(({ content }, itemIndex) => (
                <li
                  key={`item-${itemIndex}`}
                  className="font-aeonik leading-[1.2] text-2xl text-[#232323] select-none"
                >
                  {content}
                </li>
              ))}
            </ul>
          </article>
        ))}
      </div>
      <Drawer>
        {/* @ts-expect-error */}
        <Button
          text="Pre-Register"
          type="primary"
          variant="grayscale"
          typeButton={{
            className: ''
          }}
        />
      </Drawer>
    </Section>
  );
};
